/**
 * Estate detail view — mockup-parity port of vertailutyokalu.html `showTarget()`
 * (lines 8830–9020). Renders into `#targetSection`:
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ target-card                                                  │
 *   │   target-header                                              │
 *   │     left: title, "kunta · maakunta", id+deadline, status-pill│
 *   │     actions: "Siirry myynti-ilmoitukseen", "Jaa arvio"       │
 *   │   target-body                                                │
 *   │     detail-grid · 12 KPI cards                               │
 *   │     ai-analysis section                                      │
 *   │     map section (Leaflet + OSM tiles)                        │
 *   │     comparable-based valuation section                       │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Anonymous users are redirected to the register prompt before any of this
 * renders — the mockup gates the whole detail view behind authentication.
 */
import { getEstate, getAITranslation } from '../api/estates.ts';
import { estimate } from '../api/valuation.ts';
import { APIRequestError } from '../api/client.ts';
import { currentUser } from '../state/session.ts';
import { showRegisterPrompt } from './auth-modals.ts';
import { t, getLang } from '../i18n/index.ts';
import { mountEstateMap, disposeEstateMap } from './estate-map.ts';
import { bindShareButton, bindOrderButton, bindSaveButton } from './estate-actions.ts';
import { lookupInstInterest, prefetchInstInterest, type InstInfo } from '../data/inst-data.ts';
import type {
  Divergence,
  Estate,
  Recommendation,
  ScoredComparable,
  Valuation,
} from '../types/api.ts';

/**
 * Active ratio method — `asking` (default) or `summa`. Persisted across
 * re-renders so toggling between bid-grid tabs keeps the choice. Mockup
 * mirrors this with the global `RATIO_METHOD` variable in vertailutyokalu.
 */
type RatioMethod = 'asking' | 'summa';
let RATIO_METHOD: RatioMethod = 'asking';
let CURRENT_VALUATION: Valuation | null = null;
let CURRENT_ESTATE: Estate | null = null;

const locale = (): string => (getLang() === 'en' ? 'en-FI' : 'fi-FI');
const EUR = (): Intl.NumberFormat =>
  new Intl.NumberFormat(locale(), {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });
const NUM0 = (): Intl.NumberFormat =>
  new Intl.NumberFormat(locale(), { maximumFractionDigits: 0 });
const NUM1 = (): Intl.NumberFormat =>
  new Intl.NumberFormat(locale(), { maximumFractionDigits: 1 });
const fmtEUR = (v: number | undefined | null): string =>
  v == null || !isFinite(v) || v === 0 ? '—' : EUR().format(v);
const fmtHa = (v: number | undefined | null): string =>
  v == null || !isFinite(v) || v === 0 ? '—' : `${NUM1().format(v)} ha`;
const fmtM3 = (v: number | undefined | null): string =>
  v == null || !isFinite(v) || v === 0 ? '—' : `${NUM0().format(v)} m³`;
const fmtM3ha = (v: number | undefined | null): string =>
  v == null || !isFinite(v) || v === 0 ? '—' : `${NUM1().format(v)} m³/ha`;
const fmtPct = (v: number | undefined | null): string =>
  v == null || !isFinite(v) ? '—' : `${NUM1().format(v * 100)} %`;
const fmtDate = (v: string | undefined | null): string => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString(getLang() === 'en' ? 'en-GB' : 'fi-FI');
  } catch {
    return v;
  }
};

export interface DetailOptions {
  onBack: () => void;
}

// Map lifecycle is owned by `./estate-map.ts` — see disposeEstateMap().

export async function openEstate(id: number, opts: DetailOptions): Promise<void> {
  // Anonymous → register prompt, matching the mockup gate.
  if (!currentUser()) {
    showRegisterPrompt();
    return;
  }

  const listing = document.getElementById('listingSection')!;
  const search = document.querySelector<HTMLElement>('.search-section');
  const target = document.getElementById('targetSection')!;

  listing.style.display = 'none';
  if (search) search.style.display = 'none';
  target.innerHTML = `<div class="target-backbar"><button class="target-back-btn" id="detailBackBtn">${escapeHtml(t('detail_back'))}</button></div><div class="listing-empty">…</div>`;
  window.scrollTo({ top: 0, behavior: 'smooth' });

  document.getElementById('detailBackBtn')!.addEventListener('click', () => {
    disposeEstateMap();
    target.innerHTML = '';
    listing.style.display = '';
    if (search) search.style.display = '';
    opts.onBack();
  });

  try {
    // Reported 2026-05-31: level=1 (Taso 1) must order each valuation
    // separately — they see the listing + KPI/AI/map block but not the
    // valuation, comparables or method report. Exception (asiakaspalaute
    // 3.6.2026): a level 0/1 caller who was *granted* access to this estate
    // via a share or an approved order DOES see the full analysis. The
    // backend is authoritative — it returns `can_see_analysis` on the estate.
    // We optimistically fetch the valuation in parallel only when the client
    // already knows the tier qualifies (level≥2/admin/institution); for
    // granted level 0/1 callers we fetch it sequentially once the estate
    // response confirms access, avoiding a wasted call (and price leak) for
    // ungranted level-1 users.
    const tierCanSee = canSeeValuation();
    const [estate, parallelValuation] = await Promise.all([
      getEstate(id),
      tierCanSee
        ? estimate({ estate_id: id }).catch((e) => e as Error)
        : Promise.resolve(new Error('valuation gated') as Error),
    ]);
    const canSeeAnalysis = tierCanSee || estate.can_see_analysis === true;
    let valuationOrError = parallelValuation;
    if (!tierCanSee && canSeeAnalysis) {
      valuationOrError = await estimate({ estate_id: id }).catch((e) => e as Error);
    }
    target.innerHTML = renderCard(estate, valuationOrError, canSeeAnalysis);
    bindDetailEvents();
    // M6: hydrate the API-derived institutional-buyer share for this region
    // + size and re-render the buyer-mix block once the response lands. The
    // first paint uses the static fallback so the user never sees an empty
    // panel; the swap is a no-op when the API has < 10 buyer rows.
    void hydrateBuyerMix(estate);
    // M5: wire share button (always available to authenticated users) and
    // order button (rendered for level=1 callers — see renderCard).
    // Audit v2 virhe 1 (2026-05-30): one primary action per user level.
    // We bind every variant unconditionally — only the matching button is
    // present in the DOM (see primaryActionButton); the other binders are
    // cheap no-ops thanks to the `if (!btn) return;` guards.
    const estateName = estate.name || `#${estate.id}`;
    bindShareButton(estate.id, estateName);
    bindOrderButton('btnRequestOrder', estate.id, estateName);
    bindSaveButton(estate.id, estateName);
    // Kick off Ollama EN translation if needed (no-op when lang=fi or
    // when the property has no AI text). Body swap is async-safe.
    maybeFetchAITranslation(estate);
    // Map mounts after innerHTML so the container element exists. The map
    // module fetches the parcel polygon from MML by `estate.property_id`
    // and highlights it; falls back to a region-centroid marker if the
    // lookup fails.
    const mapEl = document.getElementById('estateMap');
    if (mapEl) {
      void mountEstateMap(estate, { el: mapEl });
    }
  } catch (err) {
    target.innerHTML = `<div class="target-backbar"><button class="target-back-btn" id="detailBackBtn">${escapeHtml(t('detail_back'))}</button></div><div class="listing-empty" style="color:rgb(140,55,55)">${escapeHtml(String((err as Error).message))}</div>`;
    document.getElementById('detailBackBtn')!.addEventListener('click', () => {
      target.innerHTML = '';
      listing.style.display = '';
      if (search) search.style.display = '';
      opts.onBack();
    });
  }
}

function bindDetailEvents(): void {
  // The back button is rendered both in the loading skeleton (where it was
  // wired in `openEstate`) and inside the rendered card. After innerHTML
  // replacement we need to re-bind so a click still navigates home.
  const back = document.getElementById('detailBackBtn');
  if (back) {
    back.addEventListener('click', () => {
      disposeEstateMap();
      window.location.hash = '#/';
    });
  }
  // Ratio-method toggle inside the estimate-section.
  bindRatioToggle();
}

function renderCard(
  e: Estate,
  valuationOrError: Valuation | Error,
  canSeeAnalysis: boolean,
): string {
  const isSold = !!(e.kauppahinta && e.kauppahinta > 0);
  const propId = e.property_id || `#${e.id}`;
  const dl = e.closing_date ? fmtDate(e.closing_date) : null;

  // Calculated KPIs that the mockup shows but aren't direct backend fields.
  const forestArea = e.forest_area || e.area || 0;
  const totalArea = e.area || 0;
  const price = e.price || 0;
  const woodValue = e.wood_value || 0;
  // Metsämaanhinta €/ha — asiakaspalaute 3.6.2026: aiempi (pyynti − puuston
  // arvo)/metsäala antoi virheellisen luvun (mm. "—" kun puuston arvo ylitti
  // pyynnin tai puuttui). Käytetään suoraa €/ha:ta (pyynti / metsäala), joka
  // vastaa listauksen €/ha-saraketta ja metsädatan price_per_ha-kenttää.
  const eurHa = forestArea > 0 ? price / forestArea : 0;
  const m3PerHa = e.timber_per_ha || (forestArea > 0 ? (e.timber_volume || 0) / forestArea : 0);
  const eurPerM3 = e.timber_volume && e.timber_volume > 0 && price ? price / e.timber_volume : 0;
  // Tukkipuun osuus — asiakaspalaute 3.6.2026: saw_timber_ratio on usein 0/null
  // vaikka tukkimäärä (saw_timber_m3) ja ainespuu (timber_volume) löytyvät, joten
  // johdetaan osuus niistä kun valmista suhdelukua ei ole. Molemmat ovat osuuksia
  // (0–1); fmtPct kertoo ×100.
  const sawRatio =
    e.saw_timber_ratio && e.saw_timber_ratio > 0
      ? e.saw_timber_ratio
      : e.timber_volume && e.timber_volume > 0 && e.saw_timber_m3
        ? e.saw_timber_m3 / e.timber_volume
        : null;

  return `
    <div class="target-backbar">
      <button class="target-back-btn" id="detailBackBtn" aria-label="${escapeHtml(t('detail_back'))}">
        <span class="target-back-arrow">←</span>
        <span>${escapeHtml(t('detail_back'))}</span>
      </button>
    </div>

    <div class="target-card">
      <div class="target-header">
        <div class="target-header-left">
          <!--
            Asiakaspalaute 3.6.2026: aiemmin otsikko näytti vain kunnan nimen
            (Audit v2 virhe 2 -anonymisointi), mutta asiakas piti tätä virheenä —
            listalla tilan nimi näkyy oikein, joten myös analyysisivun otsikon
            tulee näyttää tilan nimi. Level-0-anonymisointi hoidetaan jo
            backendissa (nimeä ei palauteta), joten e.name on tyhjä silloin ja
            putoaa kunta-/tunnusfallbackiin.
          -->
          <div class="target-title">${escapeHtml(e.name || e.municipality || `Tila ${propId}`)}</div>
          <div class="target-loc">${escapeHtml(e.municipality || '—')} · ${escapeHtml(String(e.region || '—'))}</div>
          <div class="target-id">${escapeHtml(propId)}${dl ? ` · ${escapeHtml(t('detail_deadline_label'))}: ${escapeHtml(dl)}` : ''}</div>
          <span class="status-pill ${isSold ? 'status-sold' : 'status-unsold'}">
            ${escapeHtml(isSold ? t('detail_status_sold') : t('detail_status_unsold'))}
          </span>
        </div>
        <div class="target-header-actions">
          ${
            e.source_url
              ? `<a href="${escapeHtml(e.source_url)}" target="_blank" rel="noopener noreferrer" class="target-listing-link"><span>${escapeHtml(t('detail_listing_link'))}</span></a>`
              : ''
          }
          <!--
            Audit v2 virhe 1 (2026-05-30): exactly one primary action per
            user level — anon shows none, level=0 with allowed_regions
            shows "Pyydä arviota", levels 1/2/3 show "Tallenna arvio",
            level=4 + admin show "Jaa arvio". The picker is in
            primaryActionButton(). The previous design rendered Share +
            RequestOrder side-by-side for everyone; the audit flagged
            this as confusing UX.
          -->
          ${primaryActionButton(e)}
        </div>
      </div>

      <div class="target-body">

        <!-- 12 KPI cards — mockup detail-grid (vertailutyokalu.html:8901) -->
        <div class="detail-grid">
          ${kpi(t('d_forest_ha'), fmtHa(forestArea))}
          ${kpi(t('d_total_ha'), fmtHa(totalArea))}
          ${kpi(t('d_asking'), fmtEUR(price))}
          ${kpi(t('d_summa'), fmtEUR(e.sum_value))}
          ${kpi(t('d_timber_value'), fmtEUR(woodValue))}
          ${kpi(t('d_eur_ha'), fmtEUR(eurHa))}
          ${kpi(t('d_m3ha'), fmtM3ha(m3PerHa))}
          ${kpi(t('d_eur_m3_asking'), eurPerM3 > 0 ? `${EUR().format(eurPerM3)} / m³` : '—')}
          ${kpi(t('d_ainespuu'), fmtM3(e.timber_volume))}
          ${kpi(t('d_tukki_m3'), fmtM3(e.saw_timber_m3))}
          ${kpi(t('d_tukki_pct'), fmtPct(sawRatio))}
          ${kpi(t('d_broker'), brokerCell(e))}
        </div>

        ${renderAIAnalysis(e)}
        ${renderMapSection(e)}
        ${
          // Reported 2026-05-31: level=1 (Taso 1) sees up to and
          // including the map; the valuation block, comparables table
          // and method report are gated to level≥2. The button picker
          // already gives level=1 a "Pyydä arviota" CTA so they can
          // order each valuation separately.
          canSeeAnalysis
            ? `
        ${renderValuationSection(e, valuationOrError)}
        ${valuationOrError instanceof Error ? '' : renderComparables(valuationOrError)}
        ${valuationOrError instanceof Error ? '' : renderMethodReport()}`
            : renderLockedAnalysis()
        }
        <!--
          Audit v2 virhe 6 (2026-05-30): the reference design does not
          render the listing's free-text "Kuvaus" / "Huomiot" blocks on the
          comparison detail page — they leak seller-supplied marketing copy
          and identifying details that the rest of the page anonymises.
          renderDescription() is kept (returns '') so the call site stays
          stable if a future spec re-enables the section.
        -->
        ${renderDescription(e)}
      </div>
    </div>
  `;
}

/**
 * Audit v2 virhe 1 (2026-05-30): renders exactly one primary action
 * button (or none) based on the caller's role + level. Mapping per the
 * audit spec:
 *
 *   anonymous         → no button
 *   role=user, lvl=0  → "Pyydä arviota"  (#btnRequestOrder)
 *   role=user, lvl=1  → "Tallenna arvio" (#btnSave)
 *   role=user, lvl=2  → "Tallenna arvio" (Pro)
 *   role=user, lvl=3  → "Tallenna arvio"
 *   role=user, lvl=4  → "Jaa arvio"      (#btnShare — Työntekijä)
 *   role=admin        → "Jaa arvio"
 *   role=institution  → "Tallenna arvio" (treated as paid tier)
 *
 * The level-to-label mapping aligns with the i18n keys (level_0="Taso 1",
 * etc.) — see admin-panel.ts and the discrepancy noted in diary.md
 * (the BE region-scope middleware also keys off `level`, but with a
 * different offset; M-FB1 only fixes the FE behaviour). The other
 * buttons are intentionally absent from the DOM so the click handlers
 * cannot fire on the wrong button.
 */
function primaryActionButton(_e: Estate): string {
  const u = currentUser();
  if (!u) return '';
  const cls = 'target-share-btn';
  if (u.role === 'admin' || u.level === 4) {
    return `<button class="${cls}" id="btnShare"><span>${escapeHtml(t('detail_share'))}</span></button>`;
  }
  // Reported 2026-05-31: levels 0 + 1 must order every valuation
  // separately (the FE hides the valuation block for them; the BE
  // valuation endpoint stays callable so a level=1 caller who has paid
  // for an order can still receive the result through that flow). The
  // "Tallenna arvio" button is reserved for paid tiers that have a
  // valuation to bookmark.
  if (u.level <= 1) {
    return `<button class="${cls}" id="btnRequestOrder"><span>${escapeHtml(t('detail_request_order'))}</span></button>`;
  }
  // levels 2 and 3 (and any non-admin institution caller) — paid tiers
  // that benefit from a personal "Tallennetut" list.
  return `<button class="${cls}" id="btnSave"><span>${escapeHtml(t('save_btn'))}</span></button>`;
}

// renderLockedAnalysis renders the "Vertailuanalyysi vain tilauksesta" notice
// below the map for signed-in callers whose tier does not expose the inline
// valuation (level 0 + 1). Asiakaspalaute 3.6.2026: aiemmin kartan alle ei
// tullut mitään, vaikka mockupissa (vertailutyokalu.html:8937) on lukittu
// ilmoitus + "Pyydä arviota" -ohje. Anonyymeille (ei kirjautunut) ei näytetä
// mitään — heidät ohjataan kirjautumaan ennen detail-näkymää.
function renderLockedAnalysis(): string {
  if (!currentUser()) return '';
  return `
    <div class="locked-analysis">
      <div class="locked-icon">🔒</div>
      <div class="locked-title">${escapeHtml(t('locked_title'))}</div>
      <div class="locked-text">${escapeHtml(t('locked_text'))}</div>
    </div>`;
}

// canSeeValuation returns true when the caller's tier exposes the
// /smt/valuation result inline on the detail page. Level≤2 = no inline
// valuation; admin / institution / level≥2 = full block.
function canSeeValuation(): boolean {
  const u = currentUser();
  if (!u) return false;
  if (u.role === 'admin' || u.role === 'institution') return true;
  return (u.level ?? 0) >= 2;
}

function kpi(label: string, value: string): string {
  return `
    <div class="detail-card">
      <div class="detail-label">${escapeHtml(label)}</div>
      <div class="detail-value">${value}</div>
    </div>
  `;
}

function brokerCell(e: Estate): string {
  const name = e.seller_name || '';
  const site = e.source_site || '';
  if (!name && !site) return '<span style="color:var(--muted)">—</span>';
  const lines: string[] = [];
  if (site) lines.push(`<div class="detail-broker-site">${escapeHtml(site)}</div>`);
  if (name) lines.push(`<div class="detail-broker-name">${escapeHtml(name)}</div>`);
  return lines.join('');
}

/**
 * AI analysis block. The source text is always Finnish (the agent
 * pipeline writes it that way). When the active locale is English we
 * render the FI text immediately AND fire an async fetch against
 * `GET /api/v1/smt/estates/:id/ai-translation` (Ollama-backed) and swap
 * the body when the translation arrives. The first render keeps the
 * page responsive and the second render shows the translation as soon
 * as it is ready (~1-3 s warm, up to 45 s cold).
 *
 * The DOM target is `[data-ai-analysis-body]` so the swap is robust to
 * other re-renders inside the same section.
 */
function renderAIAnalysis(e: Estate): string {
  const text = (e.ai_highlight_reason || '').trim();
  if (!text) {
    return `
      <section class="ai-analysis ai-analysis-empty">
        <div class="ai-analysis-title">${escapeHtml(t('ai_analysis_title'))}</div>
        <div class="ai-analysis-empty-text">${escapeHtml(t('ai_analysis_empty'))}</div>
      </section>
    `;
  }
  const lang = getLang();
  // Show a small "translating…" hint only when we are actually going to
  // fetch one. FI viewers see the original text without any badge.
  const translatingBadge =
    lang === 'en'
      ? `<span class="ai-analysis-xl-status" data-ai-xl-status>${escapeHtml(t('ai_analysis_translating'))}</span>`
      : '';
  return `
    <section class="ai-analysis" data-estate-id="${e.id}">
      <div class="ai-analysis-title">
        ${escapeHtml(t('ai_analysis_title'))}
        ${translatingBadge}
      </div>
      <div class="ai-analysis-body" data-ai-analysis-body>${escapeHtml(text)}</div>
    </section>
  `;
}

/**
 * After the detail card has rendered, kick off the EN translation fetch
 * if needed and swap the AI-analysis body in place. Called from
 * `renderInto()` once the DOM is live so we never `getElementById` on a
 * detached fragment.
 */
function maybeFetchAITranslation(e: Estate): void {
  if (getLang() !== 'en') return;
  const text = (e.ai_highlight_reason || '').trim();
  if (!text) return;
  const section = document.querySelector<HTMLElement>(
    `.ai-analysis[data-estate-id="${e.id}"]`,
  );
  if (!section) return;
  const body = section.querySelector<HTMLElement>('[data-ai-analysis-body]');
  const status = section.querySelector<HTMLElement>('[data-ai-xl-status]');
  if (!body) return;
  void getAITranslation(e.id, 'en')
    .then((translated) => {
      // Locale may have changed back to FI while the request was in
      // flight — keep the FI body in that case.
      if (getLang() !== 'en') return;
      if (translated) {
        body.textContent = translated;
      }
    })
    .finally(() => {
      if (status) status.remove();
    });
}

function renderMapSection(e: Estate): string {
  const kunta = e.municipality || (e.region as string) || '';
  const sub = kunta ? t('map_sub', { kunta }) : t('map_sub_anonymous');
  return `
    <section class="map-section">
      <div class="map-header">
        <div class="map-title">${escapeHtml(t('map_title'))}</div>
        <div class="map-sub">${escapeHtml(sub)}</div>
      </div>
      <div class="map-wrap" id="estateMap" data-estate-id="${e.id}"></div>
      <div class="map-attribution">${escapeHtml(t('map_attribution'))}</div>
    </section>
  `;
}

// ---------------------------------------------------------------------------
// Audit v2 virhe 5 (2026-05-30): Menetelmäraportti.
//
// Static accuracy/transparency section rendered below the comparables
// table. Numbers come from SPEC_FI.md §11 (LOO-based backtest, n≈4 912
// arvioita). The audit asks for a "Yhteenveto laatikossa", a comparison
// table, and a recommendation explaining why the asking-anchored
// estimate is the default. The size-class table the audit also mentions
// is intentionally omitted — SPEC_FI.md §11 doesn't include those
// numbers and we don't invent data; see diary.md (M-FB1 row).
// ---------------------------------------------------------------------------
function renderMethodReport(): string {
  // Accuracy table. Order + the "tilakohtaisesti parempi ennuste" row
  // match the customer's reference design (asiakaspalaute 31.5.2026).
  const accuracyRows: Array<[string, string, string]> = [
    ['mr_metric_mae',     '11,8 %',  '14,2 %'],
    ['mr_metric_median',  '9,0 %',   '11,1 %'],
    ['mr_metric_p10',     '54,1 %',  '46,0 %'],
    ['mr_metric_p20',     '82,9 %',  '77,6 %'],
    ['mr_metric_eur',     '17 531 €', '20 878 €'],
    ['mr_metric_winner',  '56,8 %',  '43,2 %'],
  ];
  const accuracyTbody = accuracyRows
    .map(
      ([key, asking, summa]) => `
        <tr>
          <td>${escapeHtml(t(key))}</td>
          <td class="num">${escapeHtml(asking)}</td>
          <td class="num">${escapeHtml(summa)}</td>
        </tr>`,
    )
    .join('');

  // Per-size accuracy table (SPEC_FI.md §11.2). Eight buckets covering
  // the LOO-backtest sample. EROTUS column is the asking-factor advantage
  // in percentage points (negative = summa wins; none in current data).
  const sizeRows: Array<[string, string, string, string, string]> = [
    ['mr_size_lt10',    '12',    '15,3 %', '27,7 %', '+12,3 pp'],
    ['mr_size_10_15',   '1 016', '12,1 %', '13,7 %', '+1,6 pp'],
    ['mr_size_15_20',   '940',   '11,5 %', '13,8 %', '+2,3 pp'],
    ['mr_size_20_30',   '1 127', '11,9 %', '13,9 %', '+2,0 pp'],
    ['mr_size_30_50',   '976',   '12,1 %', '14,1 %', '+2,0 pp'],
    ['mr_size_50_75',   '490',   '11,2 %', '16,2 %', '+5,0 pp'],
    ['mr_size_75_100',  '199',   '10,6 %', '14,4 %', '+3,8 pp'],
    ['mr_size_gt100',   '152',   '12,2 %', '14,6 %', '+2,4 pp'],
  ];
  const sizeTbody = sizeRows
    .map(
      ([key, n, asking, summa, diff]) => `
        <tr>
          <td>${escapeHtml(t(key))}</td>
          <td class="num">${escapeHtml(n)}</td>
          <td class="num">${escapeHtml(asking)}</td>
          <td class="num">${escapeHtml(summa)}</td>
          <td class="num">${escapeHtml(diff)}</td>
        </tr>`,
    )
    .join('');

  return `
    <section class="comp-section method-report-section">
      <div class="section-h">${escapeHtml(t('mr_title'))}</div>
      <div class="section-sub">${escapeHtml(t('mr_subtitle'))}</div>

      <div class="method-report-summary">
        <div class="method-report-summary-tag">— ${escapeHtml(t('mr_summary_tag'))} —</div>
        <div class="method-report-summary-headline"><strong>${escapeHtml(t('mr_summary_headline'))}</strong></div>
        <div class="method-report-summary-body">${escapeHtml(t('mr_summary_body'))}</div>
      </div>

      <div class="method-report-block-title">${escapeHtml(t('mr_block_accuracy'))}</div>
      <div class="listing-table-wrap">
        <table class="listing-table">
          <thead>
            <tr>
              <th>${escapeHtml(t('mr_th_metric'))}</th>
              <th class="num">${escapeHtml(t('mr_th_asking'))}</th>
              <th class="num">${escapeHtml(t('mr_th_summa'))}</th>
            </tr>
          </thead>
          <tbody>${accuracyTbody}</tbody>
        </table>
      </div>

      <div class="method-report-block-title">${escapeHtml(t('mr_block_regions'))}</div>
      <div class="method-report-paragraph">${escapeHtml(t('mr_regions_body'))}</div>

      <div class="method-report-block-title">${escapeHtml(t('mr_block_size'))}</div>
      <div class="listing-table-wrap">
        <table class="listing-table">
          <thead>
            <tr>
              <th>${escapeHtml(t('mr_size_th_class'))}</th>
              <th class="num">${escapeHtml(t('mr_size_th_n'))}</th>
              <th class="num">${escapeHtml(t('mr_size_th_asking_mae'))}</th>
              <th class="num">${escapeHtml(t('mr_size_th_summa_mae'))}</th>
              <th class="num">${escapeHtml(t('mr_size_th_diff'))}</th>
            </tr>
          </thead>
          <tbody>${sizeTbody}</tbody>
        </table>
      </div>
      <div class="method-report-paragraph">${escapeHtml(t('mr_size_body'))}</div>

      <div class="method-report-block-title">${escapeHtml(t('mr_why_title'))}</div>
      <div class="method-report-paragraph">${escapeHtml(t('mr_why_body'))}</div>

      <div class="method-report-block-title">${escapeHtml(t('mr_recommendation_title'))}</div>
      <div class="method-report-paragraph"><strong>${escapeHtml(t('mr_recommendation_lead'))}</strong> ${escapeHtml(t('mr_recommendation_body'))}</div>

      <div class="method-report-footer">${escapeHtml(t('mr_footer'))}</div>
    </section>
  `;
}

// Audit v2 virhe 6 (2026-05-30): description/remarks are intentionally
// suppressed on the detail page (see the reference mockup — neither field
// is rendered there). The function is kept as a stable call site so a
// future spec change can re-enable the section without re-threading the
// template; it returns '' for now and the helper variables explain why
// `e.description` / `e.remarks` are read but not rendered.
function renderDescription(_e: Estate): string {
  return '';
}

// ---------------------------------------------------------------------------
// Valuation section (mockup `estimate-section` — vertailutyokalu.html:8967+)
// Layout:
//   - estimate-title "Tarjous — N parasta vertailutilaa"
//   - ratio-toggle  [Pyynti-suhdeluku | Summa-arvo-suhdeluku]
//   - bid-grid      [Konservatiivinen | Suositus (mediaani) | Aggressiivinen]
//   - confidence-banner (green / yellow / red)
//   - alternative-label line (the other method's mid)
//   - divergence-notice (when methods disagree by ≥15 %)
//   - buyer-mix     (Instituutio-kiinnostus bar)
//   - recommend     (headline + reasoning + plus/minus list)
// ---------------------------------------------------------------------------

interface ActiveBand {
  basis: RatioMethod;
  anchor: number;
  ratio: number;
  mid: number;
  conservative: number;
  aggressive: number;
  nComps: number;
  anchorLabel: string;
}

/**
 * Pick the active band based on `RATIO_METHOD`. Falls back to asking when
 * summa is unavailable (e.g. target.sum_value missing).
 */
function activeBand(v: Valuation): ActiveBand {
  if (RATIO_METHOD === 'summa' && v.summa) {
    return {
      basis: 'summa',
      anchor: v.summa.anchor,
      ratio: v.summa.ratio,
      mid: v.summa.mid,
      conservative: v.summa.conservative,
      aggressive: v.summa.aggressive,
      nComps: v.summa.n_comps,
      anchorLabel: t('d_summa'),
    };
  }
  return {
    basis: 'asking',
    anchor: v.asking.anchor,
    ratio: v.asking.ratio,
    mid: v.asking.mid,
    conservative: v.asking.conservative,
    aggressive: v.asking.aggressive,
    nComps: v.asking.n_comps,
    anchorLabel: t('d_asking'),
  };
}

function renderValuationSection(estate: Estate, v: Valuation | Error): string {
  if (v instanceof Error) {
    const msg =
      v instanceof APIRequestError && v.body.code === 'insufficient_data'
        ? t('val_insufficient')
        : v instanceof APIRequestError && v.status === 403
          ? t('val_forbidden')
          : escapeHtml(v.message);
    return `<section class="estimate-section"><div class="estimate-title">${escapeHtml(t('val_title'))}</div><div class="listing-empty">${msg}</div></section>`;
  }

  // Stash for the toggle handler — re-rendered on click so we don't bind
  // listeners again. The handler is re-bound by `bindDetailEvents()`.
  CURRENT_VALUATION = v;
  CURRENT_ESTATE = estate;

  const active = activeBand(v);
  const target = v.target || estate;
  const askingPrice = target.price || estate.price || 0;
  const m3Total = target.timber_volume || 0;

  const pct = (val: number) => ((val / askingPrice - 1) * 100).toFixed(1);
  const eurPerM3 = (val: number): string =>
    m3Total > 0 ? `${EUR().format(val / m3Total)} / m³` : '';

  // Confidence banner explanation — mockup uses regional pool size +
  // similarity %. We have `n_comps` (= regional, since SQL filter is
  // already region-bound) and `avg_sim`.
  const confKey =
    v.confidence === 'high' ? 'val_conf_high'
      : v.confidence === 'mid' ? 'val_conf_mid'
        : 'val_conf_low';
  const confExplanation = t('val_conf_explanation', {
    hard: v.n_comps,
    sim: Math.round(v.avg_sim * 100),
  });

  // Alternative-method line (the OTHER mid). Mockup labels them with
  // "Vaihtoehtoinen arvio (summa-arvon kautta)" / "(pyyntihinnan kautta)".
  const otherBasis: RatioMethod = active.basis === 'asking' ? 'summa' : 'asking';
  const altMid = otherBasis === 'summa' ? v.summa?.mid : v.asking.mid;
  const altRatio = otherBasis === 'summa' ? v.summa?.ratio : v.asking.ratio;
  const altCanShow = altMid != null && altRatio != null;
  const altViaKey = otherBasis === 'summa' ? 'val_via_summa' : 'val_via_asking';

  const summaDisabled = !v.summa;

  // Check if estate is sold (has realised sale price)
  const isSold = !!(estate.kauppahinta && estate.kauppahinta > 0);
  const soldPrice = estate.kauppahinta || 0;

  // For sold estates, show "Toteutunut kauppa vs. malliarvio" view
  if (isSold) {
    // Calculate actual price vs model estimate comparison
    const actualRatio = askingPrice > 0 ? soldPrice / askingPrice : 0;
    const modelMid = active.mid;
    const diff = soldPrice - modelMid;
    const diffPct = modelMid > 0 ? ((soldPrice / modelMid - 1) * 100) : 0;

    // Determine accuracy class based on deviation
    let diffClass: 'ratio-good' | 'ratio-mid' | 'ratio-bad' = 'ratio-mid';
    if (Math.abs(diffPct) <= 5) diffClass = 'ratio-mid';
    else if (diffPct > 5) diffClass = 'ratio-good';
    else diffClass = 'ratio-bad';

    // Determine accuracy text key
    let accuracyKey: string;
    if (Math.abs(diffPct) <= 10) accuracyKey = 'val_accuracy_good';
    else if (Math.abs(diffPct) <= 25) accuracyKey = 'val_accuracy_mid';
    else accuracyKey = 'val_accuracy_bad';

    // Confidence class for banner styling
    const confidenceClass = diffClass === 'ratio-good' ? 'high' : diffClass === 'ratio-bad' ? 'low' : 'mid';

    return `
      <section class="estimate-section">
        <div class="estimate-title">${escapeHtml(t('val_actual_vs_model'))}</div>

        <div class="ratio-toggle">
          <button class="ratio-toggle-btn ${active.basis === 'asking' ? 'active' : ''}"
                  data-ratio-method="asking">${escapeHtml(t('val_btn_asking'))}</button>
          <button class="ratio-toggle-btn ${active.basis === 'summa' ? 'active' : ''}"
                  data-ratio-method="summa"
                  ${summaDisabled ? 'disabled style="opacity:0.4;cursor:not-allowed"' : ''}>${escapeHtml(t('val_btn_summa'))}</button>
        </div>

        <div class="bid-grid">
          <div class="bid-box">
            <div class="bid-label">${escapeHtml(t('val_asking_price'))}</div>
            <div class="bid-amount">${fmtEUR(askingPrice)}</div>
            ${m3Total > 0 ? `<div class="bid-eur-m3">${eurPerM3(askingPrice)}</div>` : ''}
          </div>
          <div class="bid-box recommended">
            <div class="bid-label">${escapeHtml(t('val_actual_price'))}</div>
            <div class="bid-amount">${fmtEUR(soldPrice)}</div>
            <div class="bid-pct">${escapeHtml(t('val_ratio_x', { r: actualRatio.toFixed(3) }))} · ${((actualRatio - 1) * 100).toFixed(1)}% ${escapeHtml(t('val_vs_asking_pct'))}</div>
            ${m3Total > 0 ? `<div class="bid-eur-m3">${eurPerM3(soldPrice)}</div>` : ''}
          </div>
          <div class="bid-box">
            <div class="bid-label">${escapeHtml(t('val_model_estimate'))}</div>
            <div class="bid-amount" style="color:var(--accent2)">${active ? fmtEUR(active.mid) : '—'}</div>
            <div class="bid-pct">${active ? escapeHtml(t('val_ratio_x', { r: active.ratio.toFixed(3) })) + ' · ' + escapeHtml(t('val_n_comps_many', { n: active.nComps })) : escapeHtml(t('val_no_comps'))}</div>
            ${active && m3Total > 0 ? `<div class="bid-eur-m3">${eurPerM3(active.mid)}</div>` : ''}
          </div>
        </div>

        ${active ? `
        <div class="confidence-banner confidence-${confidenceClass}" style="margin-top:14px">
          <div class="conf-dot"></div>
          <div>
            <strong>${escapeHtml(t('val_actual_price'))} ${diffPct >= 0 ? escapeHtml(t('val_overshoots')) : escapeHtml(t('val_undershoots'))} ${escapeHtml(t('val_by_pct', { pct: Math.abs(diffPct).toFixed(1) }))}</strong>
            (${escapeHtml(t('val_diff_eur', { eur: (diff >= 0 ? '+' : '') + fmtEUR(diff) }))})
            · ${escapeHtml(t('val_based_on'))}: ${escapeHtml(t('val_based_on_asking'))}
            ${escapeHtml(t(accuracyKey))}
          </div>
        </div>` : ''}

        ${renderBuyerMix(target.region as string, target.area)}
      </section>
    `;
  }

  // For active (unsold) estates, show the original "Tarjous" view
  return `
    <section class="estimate-section">
      <div class="estimate-title">${escapeHtml(t('val_offer_title_n', { n: v.n_comps }))}</div>

      <div class="ratio-toggle">
        <button class="ratio-toggle-btn ${active.basis === 'asking' ? 'active' : ''}"
                data-ratio-method="asking">${escapeHtml(t('val_btn_asking'))}</button>
        <button class="ratio-toggle-btn ${active.basis === 'summa' ? 'active' : ''}"
                data-ratio-method="summa"
                ${summaDisabled ? 'disabled style="opacity:0.4;cursor:not-allowed"' : ''}>${escapeHtml(t('val_btn_summa'))}</button>
      </div>

      <div class="bid-grid">
        <div class="bid-box">
          <div class="bid-label">${escapeHtml(t('val_bid_conservative'))}</div>
          <div class="bid-amount">${fmtEUR(active.conservative)}</div>
          <div class="bid-pct">${pct(active.conservative)}% ${escapeHtml(t('val_vs_asking_pct'))}</div>
          ${m3Total > 0 ? `<div class="bid-eur-m3">${eurPerM3(active.conservative)}</div>` : ''}
        </div>
        <div class="bid-box recommended">
          <div class="bid-label">${escapeHtml(t('val_bid_recommended'))}</div>
          <div class="bid-amount">${fmtEUR(active.mid)}</div>
          <div class="bid-pct">${pct(active.mid)}% ${escapeHtml(t('val_vs_asking_pct'))} · ${escapeHtml(t('val_ratio_x', { r: active.ratio.toFixed(3) }))}</div>
          ${m3Total > 0 ? `<div class="bid-eur-m3">${eurPerM3(active.mid)}</div>` : ''}
        </div>
        <div class="bid-box">
          <div class="bid-label">${escapeHtml(t('val_bid_aggressive'))}</div>
          <div class="bid-amount">${fmtEUR(active.aggressive)}</div>
          <div class="bid-pct">${pct(active.aggressive)}% ${escapeHtml(t('val_vs_asking_pct'))}</div>
          ${m3Total > 0 ? `<div class="bid-eur-m3">${eurPerM3(active.aggressive)}</div>` : ''}
        </div>
      </div>

      <div class="confidence-banner confidence-${escapeHtml(v.confidence)}">
        <div class="conf-dot"></div>
        <div>
          <strong>${escapeHtml(t(confKey))}</strong> — ${escapeHtml(confExplanation)}
          · ${escapeHtml(t('val_based_on'))}: <strong style="color:var(--text)">${escapeHtml(active.anchorLabel)}</strong> ${fmtEUR(active.anchor)}
        </div>
      </div>

      ${
        altCanShow
          ? `<div style="margin-top:10px;font-family:var(--mono);font-size:11px;color:var(--muted)">
               ${escapeHtml(t('val_alternative_label'))} (${escapeHtml(t(altViaKey))}):
               <strong style="color:var(--text)">${fmtEUR(altMid as number)}</strong>
               (${escapeHtml(t('val_ratio_x', { r: (altRatio as number).toFixed(3) }))})
             </div>`
          : ''
      }

      ${renderDivergenceNotice(v.divergence)}
      ${renderBuyerMix(target.region as string, target.area)}
      ${renderRecommendation(target, v, active)}
    </section>
  `;
}

/**
 * Top-20 comparables table per SPEC_FI_FULL.md §4.
 * Backend returns up to 30 ranked comparables; we display only the 20 best
 * to keep the section scannable. Each row shows the soft-similarity score
 * as a 0-100 % bar, plus colour-coded asking and summa ratios.
 */
function renderComparables(v: Valuation): string {
  const rows = (v.comparables || []).slice(0, 20);
  if (!rows.length) return '';
  return `
    <section class="comp-section">
      <div class="section-h">${escapeHtml(t('comp_title_n', { n: rows.length }))}</div>
      <div class="section-sub">${escapeHtml(t('comp_subtitle_top20'))}</div>
      <div class="listing-table-wrap">
      <!--
        Audit v2 virhe 3 (2026-05-30): the reference design specifies 11
        columns starting with "Kunta" and ending with "€/m³". The
        previous "Tila" column re-identified an anonymised property and
        "Pvm" was an extra column not present in the spec. Added €/m³
        (kauppahinta / timber_volume) as the final column.
      -->
      <table class="listing-table comparables-table">
        <thead>
          <tr>
            <th>${escapeHtml(t('comp_th_municipality'))}</th>
            <th>${escapeHtml(t('comp_th_score'))}</th>
            <th class="num">${escapeHtml(t('comp_th_area'))}</th>
            <th class="num">${escapeHtml(t('comp_th_m3ha'))}</th>
            <th class="num">${escapeHtml(t('comp_th_tukki'))}</th>
            <th class="num">${escapeHtml(t('comp_th_price'))}</th>
            <th class="num">${escapeHtml(t('comp_th_kauppahinta'))}</th>
            <th class="num">${escapeHtml(t('comp_th_ratio'))}</th>
            <th class="num">${escapeHtml(t('comp_th_summa_ratio'))}</th>
            <th class="num">${escapeHtml(t('comp_th_eur_ha'))}</th>
            <th class="num">${escapeHtml(t('comp_th_eur_m3'))}</th>
          </tr>
        </thead>
        <tbody>
          ${rows.map(renderCompRow).join('')}
        </tbody>
      </table>
      </div>
    </section>
  `;
}

function renderCompRow(c: ScoredComparable): string {
  const area = c.forest_area || c.area;
  const m3ha = c.timber_per_ha || (area && c.timber_volume ? c.timber_volume / area : 0);
  const eurHa = area && c.kauppahinta ? c.kauppahinta / area : 0;
  // Audit v2 virhe 3 (2026-05-30): €/m³ replaces the previous Pvm column.
  // Computed only when timber_volume > 0 to avoid divide-by-zero noise on
  // properties whose timber data we never scraped.
  const eurM3 = c.timber_volume && c.timber_volume > 0 && c.kauppahinta ? c.kauppahinta / c.timber_volume : 0;
  // Sama fallback kuin tilan KPI-rivillä (3.6.2026): saw_timber_ratio on usein
  // 0/null vaikka tukkimäärä ja ainespuu löytyvät → johdetaan osuus niistä.
  const tukki =
    c.saw_timber_ratio && c.saw_timber_ratio > 0
      ? c.saw_timber_ratio * 100
      : c.timber_volume && c.timber_volume > 0 && c.saw_timber_m3
        ? (c.saw_timber_m3 / c.timber_volume) * 100
        : null;
  const askingRatio = c.kauppahinta && c.price ? c.kauppahinta / c.price : null;
  const summaRatio = c.kauppahinta && c.sum_value ? c.kauppahinta / c.sum_value : null;
  const scorePct = Math.round((c.score || 0) * 100);

  return `
    <tr data-id="${c.id}">
      <td>${escapeHtml(c.municipality || '—')}</td>
      <td class="comp-score-cell">
        <div class="comp-score-bar">
          <div class="comp-score-bar-fill" style="width:${scorePct}%"></div>
          <span class="comp-score-text">${scorePct}%</span>
        </div>
      </td>
      <td class="num">${area ? NUM1().format(area) + ' ha' : '—'}</td>
      <td class="num">${m3ha ? NUM0().format(m3ha) : '—'}</td>
      <td class="num">${tukki != null ? NUM1().format(tukki) + ' %' : '—'}</td>
      <td class="num">${fmtEUR(c.price)}</td>
      <td class="num">${fmtEUR(c.kauppahinta)}</td>
      <td class="num ratio-cell ${ratioClass(askingRatio, 'asking')}">${formatRatio(askingRatio)}</td>
      <td class="num ratio-cell ${ratioClass(summaRatio, 'summa')}">${formatRatio(summaRatio)}</td>
      <td class="num">${eurHa ? NUM0().format(eurHa) : '—'}</td>
      <td class="num">${eurM3 > 0 ? NUM1().format(eurM3) + ' €' : '—'}</td>
    </tr>
  `;
}

function formatRatio(r: number | null): string {
  if (r == null || !isFinite(r)) return '—';
  return `${r.toFixed(3)}x`;
}

/**
 * Colour-coded ratio buckets per SPEC_FI_FULL.md §4.2:
 *   asking: ≥ 1.10 → good (green); < 0.95 → bad (red); else mid (yellow)
 *   summa : ≥ 1.10 → good; < 0.90 → bad (different threshold); else mid
 */
function ratioClass(r: number | null, basis: 'asking' | 'summa'): string {
  if (r == null || !isFinite(r)) return '';
  if (r >= 1.1) return 'ratio-good';
  const lower = basis === 'summa' ? 0.9 : 0.95;
  if (r < lower) return 'ratio-bad';
  return 'ratio-mid';
}

// ---------------------------------------------------------------------------
// Divergence notice (mockup `renderDivergenceNotice` — vertailutyokalu §6)
// ---------------------------------------------------------------------------

function renderDivergenceNotice(d: Divergence | null | undefined): string {
  if (!d) return '';
  const pct = NUM1().format(d.div_pct);
  // Mockup labels: "div_title", "div_asking_higher" / "div_summa_higher",
  // "div_backtest_note", "div_suggestion". The first three already exist
  // (legacy keys). We render the same structure as the mockup: gold icon +
  // headline + multi-sentence body with bold final adjustment.
  const titleKey = 'div_title';
  const titleVars: Record<string, string | number> = { pct };
  const bodyKey = d.asking_higher ? 'div_msg_asking_higher' : 'div_msg_summa_higher';
  const body = t(bodyKey, { pct, bump: d.suggested_bump });
  return `
    <div class="divergence-notice">
      <div class="dn-icon">ⓘ</div>
      <div class="dn-body">
        <div class="dn-title">${escapeHtml(t(titleKey, titleVars))}</div>
        <div class="dn-text">${escapeHtml(body)}</div>
      </div>
    </div>`;
}

// ---------------------------------------------------------------------------
// Buyer-mix bar (mockup `renderBuyerMix` — Instituutio-kiinnostus)
// SPEC_FI_FULL.md §6. The frontend embeds INST_DATA from the mockup as a
// static JSON blob until the backend exposes the same aggregate (M6).
// ---------------------------------------------------------------------------

function renderBuyerMix(maakunta: string | null | undefined, sizeHa: number | null | undefined): string {
  if (!maakunta) return '';
  const info = lookupInstInterest(maakunta, sizeHa ?? null, t);
  if (!info) return '';

  const { barColor, label } = buyerMixStyle(info);
  const pctRounded = info.pct.toFixed(0);
  const fillPct = Math.min(100, info.pct);
  const showInsideText = info.pct >= 15;

  return `
    <div class="buyer-mix">
      <div class="bm-label">${t('inst_label')}</div>
      <div class="bm-bar-wrap">
        <div class="bm-bar-fill" style="width:${fillPct}%;background:${barColor};border-right-color:${barColor}">
          ${showInsideText ? `<span class="bm-bar-text">${escapeHtml(pctRounded)} %</span>` : ''}
        </div>
        ${
          !showInsideText
            ? `<div class="bm-bar-text-light" style="left:${info.pct + 1}%">${escapeHtml(pctRounded)} %</div>`
            : ''
        }
      </div>
      <div class="bm-comment">${escapeHtml(label)}</div>
    </div>
    <div style="margin-top:6px;font-family:var(--mono);font-size:10px;color:var(--muted);line-height:1.5">
      ${escapeHtml(info.comment)}
      <span style="color:var(--mute2)">${escapeHtml(t('inst_n_eq', { n: info.sampleN }))}</span>
    </div>`;
}

function buyerMixStyle(info: InstInfo): { barColor: string; label: string } {
  switch (info.category) {
    case 'inst_strong':
      return { barColor: 'var(--accent)', label: t('inst_focus_area') };
    case 'inst_likely':
      return { barColor: 'var(--accent)', label: t('inst_likely') };
    case 'borderline':
      return { barColor: 'rgb(187,160,69)', label: t('inst_borderline') };
    case 'inst_unlikely':
      return { barColor: 'var(--bad)', label: t('inst_unlikely') };
    case 'private':
      return { barColor: 'var(--bad)', label: t('inst_private') };
    default:
      return { barColor: 'rgb(187,160,69)', label: t('inst_avg') };
  }
}

// ---------------------------------------------------------------------------
// Recommendation block (mockup `renderRecommendation` — vertailutyokalu §5)
// Headline-driven layout: ratio sentence + strategy + reasoning + plus/minus
// bullet lists. Uses the backend-provided `Recommendation` (already populated
// from the same 8 rules) but augments it with rule 1 (lookupInstInterest)
// client-side because the backend doesn't have INST_DATA yet.
// ---------------------------------------------------------------------------

function renderRecommendation(target: Estate, v: Valuation, active: ActiveBand): string {
  const baseRec = v.recommendation || { pluses: [], minuses: [], strategy: 'balanced' as const };
  // Clone so we can prepend rule-1 entries without mutating the cached
  // Valuation.
  const pluses: string[] = [...baseRec.pluses];
  const minuses: string[] = [...baseRec.minuses];

  // Rule 1 — Buyer-mix (frontend-only, M5/M6 will move to backend).
  const inst = lookupInstInterest(
    (target.region as string) || '',
    (target.area as number) ?? null,
    t,
  );
  if (inst) {
    const pct = inst.pct.toFixed(0);
    const thrSafe = inst.threshold ?? 0;
    if (inst.category === 'inst_strong') {
      pluses.unshift(t('r_inst_strong', { pct }));
    } else if (inst.category === 'inst_likely') {
      pluses.unshift(t('r_inst_likely', { threshold: thrSafe, pct }));
    } else if (inst.category === 'inst_unlikely') {
      minuses.unshift(t('r_inst_unlikely', { threshold: thrSafe, pct }));
    } else if (inst.category === 'private') {
      minuses.unshift(t('r_inst_private', { pct: inst.regionOverall.toFixed(0) }));
    }
    // 'borderline' / 'unknown' → no entry
  }

  // Rebuild the strategy because we may have added inst entries.
  let strategy: Recommendation['strategy'];
  if (pluses.length > minuses.length + 1) strategy = 'aggressive';
  else if (minuses.length > pluses.length + 1) strategy = 'conservative';
  else strategy = 'balanced';

  // Headline ratio text — colour & wording match mockup §5.3.
  const ratio = active.ratio;
  const basisLabel = active.basis === 'summa' ? t('of_summa') : t('of_asking');
  const methodNoun = active.basis === 'summa' ? t('reason_method_summa') : t('reason_method_asking');
  let ratioText: string;
  let ratioColor: string;
  if (ratio >= 1.05) {
    ratioText = t('pct_above', { pct: ((ratio - 1) * 100).toFixed(0), basis: basisLabel });
    ratioColor = 'var(--accent2)';
  } else if (ratio >= 0.95) {
    ratioText = t('near_basis', { basis: basisLabel });
    ratioColor = 'var(--warn)';
  } else {
    ratioText = t('pct_below', { pct: ((1 - ratio) * 100).toFixed(0), basis: basisLabel });
    ratioColor = 'var(--bad)';
  }

  let strategyHtml: string;
  if (strategy === 'aggressive') {
    strategyHtml = t('strategy_aggressive_html', { eur: fmtEUR(active.aggressive) });
  } else if (strategy === 'conservative') {
    strategyHtml = t('strategy_conservative_html', { eur: fmtEUR(active.conservative) });
  } else {
    strategyHtml = t('strategy_balanced_html', { eur: fmtEUR(active.mid) });
  }

  const methodExplain =
    active.basis === 'summa'
      ? t('method_explain_summa_html', { n: active.nComps })
      : t('method_explain_asking_html', { n: active.nComps });

  const plusList =
    pluses.length > 0
      ? `<div class="rec-text" style="margin-bottom:6px"><strong style="color:var(--accent2)">${escapeHtml(t('bid_higher_if'))}</strong></div>
         <ul class="rec-list">
           ${pluses.map((p) => `<li>${escapeHtml(p)}</li>`).join('')}
         </ul>`
      : '';

  const minusList =
    minuses.length > 0
      ? `<div class="rec-text" style="margin-top:12px;margin-bottom:6px"><strong style="color:var(--bad)">${escapeHtml(t('bid_lower_if'))}</strong></div>
         <ul class="rec-list">
           ${minuses.map((m) => `<li class="minus">${escapeHtml(m)}</li>`).join('')}
         </ul>`
      : '';

  const noSignals =
    pluses.length === 0 && minuses.length === 0
      ? `<div class="rec-text" style="color:var(--muted)">${escapeHtml(t('no_signals'))}</div>`
      : '';

  return `
    <div class="recommend">
      <div class="rec-label">${escapeHtml(t('reason_label'))} · ${escapeHtml(methodNoun)}</div>
      <div class="rec-headline">
        ${escapeHtml(t('median_estimate_is'))}
        <span style="color:${ratioColor}">${escapeHtml(ratioText)}</span>
        ${escapeHtml(t('ratio_coef', { r: ratio.toFixed(3) }))}
        ${/* HTML — strategyHtml contains <strong> tags from i18n */ strategyHtml}
      </div>
      <div class="rec-text">
        ${/* HTML — methodExplain contains <strong> tags from i18n */ methodExplain}
        ${escapeHtml(t('spread_explain'))}
      </div>
      ${plusList}
      ${minusList}
      ${noSignals}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Toggle handler — re-renders the active section when the user picks a
// different ratio basis. `bindDetailEvents()` re-binds every render.
// ---------------------------------------------------------------------------

// M6: hydrate buyer-mix block from /smt/market/inst-interest. The first
// paint uses the static INST_DATA fallback (from data/inst-data.json) so the
// user never sees a blank panel; once the API responds we re-render the
// estimate-section so both renderBuyerMix() and renderRecommendation() pick
// up the fresh numbers from the module-level `apiOverrides` cache.
async function hydrateBuyerMix(estate: Estate): Promise<void> {
  const region = (estate.region as string) || '';
  const sizeHa = (estate.area as number) ?? null;
  if (!region) return;
  const updated = await prefetchInstInterest(region, sizeHa, t);
  if (!updated || !CURRENT_ESTATE || !CURRENT_VALUATION) return;
  const section = document.querySelector('.estimate-section');
  if (!section) return;
  const html = renderValuationSection(CURRENT_ESTATE, CURRENT_VALUATION);
  const tpl = document.createElement('template');
  tpl.innerHTML = html.trim();
  const fresh = tpl.content.firstElementChild;
  if (fresh && section.parentElement) {
    section.replaceWith(fresh);
    bindRatioToggle();
  }
}

function bindRatioToggle(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-ratio-method]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (btn.disabled) return;
      const method = btn.getAttribute('data-ratio-method') as RatioMethod | null;
      if (!method || method === RATIO_METHOD) return;
      RATIO_METHOD = method;
      if (!CURRENT_VALUATION || !CURRENT_ESTATE) return;
      // Replace just the estimate-section to avoid tearing down the map.
      const section = document.querySelector('.estimate-section');
      if (!section) return;
      const html = renderValuationSection(CURRENT_ESTATE, CURRENT_VALUATION);
      const tpl = document.createElement('template');
      tpl.innerHTML = html.trim();
      const fresh = tpl.content.firstElementChild;
      if (fresh && section.parentElement) {
        section.replaceWith(fresh);
        bindRatioToggle();
      }
    });
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
